import {
  Box,
  Card,
  CardActionArea,
  LinearProgress,
  Link,
  List,
  Stack,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import ChatOption from '@/karehero-app/tell-us-once/intake/ChatOption';
import ConversationRow from '@/karehero-app/tell-us-once/intake/ConversationRow';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';
import {
  HOME_CONCEPT_2,
  HOME_CONCEPT_4,
  HOME_HISTORY,
} from '@/karehero-app/tell-us-once/intake/content.v2-0';

const noop = () => {};

/** An icon in a rounded square, the way each card on this page leads. */
const IconTile = ({ icon }: { icon: string }) => (
  <Box
    sx={{
      display: 'grid',
      placeItems: 'center',
      width: 44,
      height: 44,
      flexShrink: 0,
      border: 1,
      borderColor: 'custom.neutral.200',
      borderRadius: `${radius.md}px`,
      color: 'text.primary',
    }}
  >
    <Icon icon={icon as never} fontSize={18} />
  </Box>
);

/**
 * Home, concept 2: upcoming calls, then the conversations, on the page rather
 * than in a drawer. After a notes app's home: what is coming up in a card at
 * the top, then the list under a quiet label. No calls yet, so the
 * card says so and offers to book one.
 *
 * The page only: it sits in an empty chat (ChatV2's `emptyState`), so the
 * composer under it is how a new conversation starts, with no row for it.
 */
const HomeConcept2 = ({
  todo = false,
  todoList,
  conversations = true,
  eligible = false,
  onEligible,
}: {
  /** With what onboarding still needs, as its checklist. */
  todo?: boolean;
  /**
   * A different checklist in the same card, in place of what onboarding still
   * needs. With `onTap` the card is a way in: tapping it starts the
   * conversation that does the thing, like the benefits calculator.
   */
  todoList?: {
    label: string;
    /** A line under the label, before the items: why these, for them. */
    body?: string;
    /** Empty for a card that is one thing to start, with `action` instead. */
    items: string[];
    /**
     * A button that starts it, in place of the checklist: one thing to do is
     * a button, not a list of one with a circle that reads as progress.
     */
    action?: string;
    /**
     * Several things to start, as the chat's own answer rows (ChatOption) in
     * place of the checklist, so they read as choices rather than progress.
     */
    options?: string[];
    onTap?: () => void;
    /** Each item its own way in, in place of the whole card. */
    onItem?: (index: number) => void;
    /** A link under the items, to the rest of what there is. */
    more?: string;
    /**
     * A way in, not something the AI worked out: no sparkle, and in the type
     * of Home's own cards.
     */
    plain?: boolean;
  };
  /**
   * What they could get, each with how far along it is, in place of the
   * bare list: the reason for sending anything. Tapping one opens what is
   * still needed for it.
   */
  eligible?: boolean;
  onEligible?: (title: string) => void;
  /** Off where the conversations are in the bar's menu instead. */
  conversations?: boolean;
}) => (
  <Stack>
    <Box>
      <Card variant='padded'>
        <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
          <IconTile icon='calendar' />
          <Stack>
            {/* subtitle1 brings grey of its own, so charcoal is set here. */}
            <Typography sx={{ color: 'text.primary' }} variant='subtitle1'>
              {HOME_CONCEPT_2.noCalls}
            </Typography>
            <Typography sx={{ color: 'text.primary' }} variant='body2'>
              {/* A button takes its own font and sits on its own line box, so
                  it rode above the sentence. Inherit both, on the baseline. */}
              <Link
                component='button'
                type='button'
                onClick={noop}
                sx={{ font: 'inherit', verticalAlign: 'baseline' }}
              >
                {HOME_CONCEPT_2.bookCall}
              </Link>
              {HOME_CONCEPT_2.bookCallAfter}
            </Typography>
          </Stack>
        </Stack>
      </Card>
    </Box>

    {/* What onboarding still needs, as the checklist the chat pinned when
        it asked: the same card, the first one marked as next. */}
    {eligible && (
      <Stack sx={{ pt: 3 }} spacing={1.5}>
        <Typography sx={{ color: 'text.primary' }} variant='subtitle1'>
          {HOME_CONCEPT_4.heading}
        </Typography>
        {HOME_CONCEPT_4.items.map((item) => (
          <Card key={item.title} variant='padded'>
            <CardActionArea
              onClick={() => onEligible?.(item.title)}
              sx={{ borderRadius: `${radius.md}px` }}
            >
              <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
                <IconTile icon={item.icon} />
                <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
                  <Typography
                    sx={{ color: 'text.primary' }}
                    variant='subtitle1'
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: 'text.primary' }} variant='body2'>
                    {item.detail}
                  </Typography>
                  {item.progress !== undefined && (
                    <Stack
                      sx={{ alignItems: 'center', pt: 0.5 }}
                      direction='row'
                      spacing={1}
                    >
                      <LinearProgress
                        sx={{ flex: 1 }}
                        variant='determinate'
                        value={item.progress}
                      />
                      <Typography
                        sx={{ color: 'text.primary' }}
                        variant='caption'
                      >
                        {item.progress}%
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                  <Icon icon='chevron-right' fontSize={14} />
                </Box>
              </Stack>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    )}

    {todo && (
      <Box sx={{ pt: 3 }}>
        {todoList?.onTap ? (
          <CardActionArea
            onClick={todoList.onTap}
            sx={{ borderRadius: `${radius.md}px` }}
          >
            <FromUsCard
              label={todoList.label}
              body={todoList.body}
              sentence
              items={todoList.items}
              checked={-1}
            />
          </CardActionArea>
        ) : (
          <FromUsCard
            label={todoList?.label ?? HOME_CONCEPT_2.todo}
            body={todoList?.body}
            sentence
            items={
              todoList?.action || todoList?.options
                ? undefined
                : (todoList?.items ?? HOME_CONCEPT_2.todos)
            }
            checked={todoList ? -1 : 0}
            onItem={todoList?.onItem}
            sparkle={!todoList?.plain}
            asHomeCard={todoList?.plain}
          >
            {todoList?.options && (
              <Stack spacing={1}>
                {todoList.options.map((option) => (
                  <ChatOption key={option} label={option} onClick={noop} />
                ))}
              </Stack>
            )}
            {todoList?.action && (
              <Button
                variant='primary'
                isFullWidth
                ariaLabel={todoList.action}
                onPress={noop}
              >
                {todoList.action}
              </Button>
            )}
            {todoList?.more && (
              <Link
                component='button'
                type='button'
                onClick={noop}
                sx={{ alignSelf: 'flex-start' }}
                variant='body2'
              >
                {todoList.more}
              </Link>
            )}
          </FromUsCard>
        )}
      </Box>
    )}

    {conversations && (
      <Stack sx={{ pt: 3 }} spacing={0.5}>
        <Typography sx={{ color: 'text.primary' }} variant='subtitle1'>
          {HOME_CONCEPT_2.conversations}
        </Typography>
        {/* The rows have no side padding (ConversationRow's `tile`), so the
          tiles start on the label's edge. */}
        <List>
          {HOME_HISTORY.threads.map((t) => (
            <ConversationRow
              key={t.title}
              label={t.title}
              tile
              onClick={noop}
            />
          ))}
        </List>
      </Stack>
    )}
  </Stack>
);

export default HomeConcept2;
